import useEnv from '@root/shared/useEnv';
import api from '@shared/api';
import {
  UploadApiResponse as CloudinaryResponse,
  UploadApiErrorResponse as CloudinaryErrorResponse,
} from 'cloudinary';
import { useRef } from 'react';
import { MIME_TYPES } from '../Admin/MultimediaUploader';

type SelectAndUploadToCloudinaryArgs = {
  callback?: (response: CloudinaryResponse) => void;
};

type Output = {
  uploadToCloudinary: (file) => void;
  selectAndUploadToCloudinary?: (args?: SelectAndUploadToCloudinaryArgs) => void;
  CloudinaryUploaderFileInput: typeof CloudinaryUploaderFileInput;
  cloudinaryUploaderFileInputProps: CloudinaryUploaderFileInputProps;
};

export default function useCloudinaryUploader({
  uploadToVTS = false,
  onReady,
  onError,
}: {
  uploadToVTS?: boolean;
  onReady?: (response: CloudinaryResponse) => void;
  onError: (message: string) => void;
}): Output {
  const fileInput = useRef<HTMLInputElement>(null);
  const onReadyCallbackRef = useRef<((response: CloudinaryResponse) => void) | null>(null);

  const {
    cloudinaryCloud,
    cloudinaryUploadPreset,
    cloudinaryVtsImageUploadPreset,
    cloudinaryVtsImageCloud,
  } = useEnv();

  const cloudName = uploadToVTS ? cloudinaryVtsImageCloud : cloudinaryCloud;
  const cloudinaryPreset = uploadToVTS ? cloudinaryVtsImageUploadPreset : cloudinaryUploadPreset;

  const uploadToCloudinary = async file => {
    const formData = new FormData();
    formData.append('file', file[0]);
    formData.append('upload_preset', cloudinaryPreset || '');

    const route = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
    const response = await api.fetch(route, { method: 'POST', body: formData });
    if (response.ok) {
      const json: CloudinaryResponse = await response.json();
      if (onReady) onReady(json);
      if (onReadyCallbackRef.current) onReadyCallbackRef.current(json);
    } else {
      const json: CloudinaryErrorResponse = await response.json();
      onError(json.message);
    }
  };

  return {
    uploadToCloudinary,
    selectAndUploadToCloudinary: ({ callback }: SelectAndUploadToCloudinaryArgs = {}) => {
      fileInput?.current?.click();
      onReadyCallbackRef.current = callback || null;
    },
    CloudinaryUploaderFileInput,
    cloudinaryUploaderFileInputProps: {
      inputRef: fileInput,
      uploadToCloudinary,
    },
  };
}

type CloudinaryUploaderFileInputProps = {
  inputRef: React.ForwardedRef<HTMLInputElement>;
  uploadToCloudinary: (file) => void;
  mimetypes?: string;
};
const CloudinaryUploaderFileInput = ({
  inputRef,
  uploadToCloudinary,
  mimetypes = MIME_TYPES.imageOnly,
}: CloudinaryUploaderFileInputProps) => {
  return (
    <input
      ref={inputRef}
      type="file"
      className="hidden"
      onChange={async event => {
        const file = event.target.files;
        await uploadToCloudinary(file);
      }}
      accept={mimetypes}
      data-testid="cloudinaryUploaderFileInput"
    />
  );
};
